import { format, isValid, parseISO } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EventLocation, EventLocationType } from '../../../services';
import { markdownToHtml } from '../../utils';
import { ChangeLogChange } from './ChangeLogModal';

export const CHANGE_LOG_DATE_FORMAT = 'dd MMM yyyy, HH:mm';

const BOOLEAN_FIELDS = [
  'published',
  'includeInNewsFeed',
  'includeInWebappNewsFeed',
  'optionAllowDisplayAuthor',
  'optionAllowComment',
  'optionAllowSharing',
  'optionAllowCardReactions',
  'optionShouldUpdatePostedTimeByComments',
  'pollAllowAnonymous',
  'pollMultipleVotes',
  'pollShowVoteResults',
  'pollShowTotalVotes',
  'showParticipants',
  'allowWaitlist',
];

const IMAGE_FIELDS = ['contentQuotePersonImage', 'image'];

const GALLERY_FIELDS = ['contentGallery', 'gallery'];

const TEXT_FIELDS = [
  'styleBackgroundColor',
  'type',
  'locale',
  'contentHeadline',
  'contentSubHeadline',
  'contentTitle',
  'contentAbstract',
  'contentSourceName',
  'contentAuthor',
  'contentQuote',
  'contentQuoteSource',
  'contentQuoteCreated',
  'contentQuotePerson',
  'contentQuotePersonHandle',
  'contentUrl',
  'contentReadTime',
  'styleTeaserImageStyle',
  'status',
  'name',
  'externalUrl',
  'externalLabel',
  'participantsCapacity',
  'reminderHoursBeforeStart',
];

const MARKDOWN_FIELDS = ['contentText', 'description'];

const DATE_FIELDS = [
  'postedTime',
  'pollVotingTime',
  'startAt',
  'endAt',
  'scheduledAt',
  'participantsRegistrationDeadlineEndAt',
];

const ARRAY_FIELDS: Record<string, string> = {
  organisationExternalTags: 'name',
  organisationExternalStatusTags: 'name',
  organisationInternalTags: 'name',
  mentionedUsers: 'screenName',
  pollOptions: 'text',
  channels: 'name',
  hosts: 'screenName',
  organisationTags: 'name',
};

interface GalleryChangeItem {
  id: number;
  title?: string;
  image?: { id: number; rightholder?: string } | null;
  audio?: { id: number } | null;
  video?: { id: number } | null;
  pdf?: { id: number } | null;
}

interface TextChangeParams {
  label: string;
  from: unknown;
  to: unknown;
  allowHtml?: boolean;
}

const hasNonImageMedia = ({ audio, video, pdf }: GalleryChangeItem) =>
  Boolean(audio ?? video ?? pdf);

const withExternalLinks = (htmlString: string) =>
  htmlString.replace(/href=/g, 'target="_blank" rel="noopener noreferrer" href=');

const toContentHtml = (markdown: unknown): string => {
  if (!markdown) {
    return '';
  }

  return withExternalLinks(markdownToHtml({ markdown: markdown as string }));
};

const toFormattedDate = (value: unknown): string => {
  if (!value) {
    return '';
  }

  const date = parseISO(value as string);

  if (!isValid(date)) {
    return String(value);
  }

  return format(date, CHANGE_LOG_DATE_FORMAT);
};

export const useFormatChange = () => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'changeLogFields' });

  const { t } = useTranslation();

  const textChange = useCallback(
    ({ label, from, to, allowHtml }: TextChangeParams): string => {
      const interpolation = { escapeValue: !allowHtml };

      if (!from) {
        return ctxT('textSet', { label, to, interpolation });
      }

      if (!to) {
        return ctxT('textRemoved', { label });
      }

      return ctxT('textChanged', { label, from, to, interpolation });
    },
    [ctxT]
  );

  const toLocationText = useCallback(
    (value: unknown): string => {
      if (!value) {
        return '';
      }

      const { type, address, virtualUrl } = value as EventLocation;

      const interpolation = { escapeValue: false };

      if (type === EventLocationType.IN_PERSON) {
        return t('eventItem.in-person', { address, interpolation });
      }

      if (type === EventLocationType.VIRTUAL) {
        return t('eventItem.online', { url: virtualUrl, interpolation });
      }

      return t('createEvent.no-location');
    },
    [t]
  );

  return useCallback(
    ({ path, from, to }: ChangeLogChange): string => {
      const label = ctxT(path);

      const arrayChange = (nameKey: string): string => {
        const fromArr = (from as Record<string, unknown>[]) ?? [];
        const toArr = (to as Record<string, unknown>[]) ?? [];
        const added = toArr.filter(({ id }) => !fromArr.some((item) => item.id === id));
        const removed = fromArr.filter(({ id }) => !toArr.some((item) => item.id === id));

        const renamed = toArr.flatMap((item) => {
          const previous = fromArr.find(({ id }) => id === item.id);

          if (!previous || previous[nameKey] === item[nameKey]) {
            return [];
          }

          return `"${String(previous[nameKey])}" → "${String(item[nameKey])}"`;
        });

        const parts: string[] = [];

        if (added.length > 0) {
          parts.push(
            ctxT('itemsAdded', {
              names: added.map((item) => `"${String(item[nameKey])}"`).join(', '),
              label,
              interpolation: { escapeValue: false },
            })
          );
        }

        if (removed.length > 0) {
          parts.push(
            ctxT('itemsRemoved', {
              names: removed.map((item) => `"${String(item[nameKey])}"`).join(', '),
              label,
              interpolation: { escapeValue: false },
            })
          );
        }

        if (renamed.length > 0) {
          parts.push(ctxT('itemsRenamed', { names: renamed.join(', '), label }));
        }

        return parts.join(', ');
      };

      const galleryChange = (): string => {
        const fromArr = (from as GalleryChangeItem[]) ?? [];
        const toArr = (to as GalleryChangeItem[]) ?? [];
        const added = toArr.filter(({ id }) => !fromArr.some((item) => item.id === id)).length;
        const removed = fromArr.filter(({ id }) => !toArr.some((item) => item.id === id)).length;

        const matched = fromArr.flatMap((from) =>
          toArr.filter(({ id }) => id === from.id).map((to) => ({ from, to }))
        );

        const nonImageMedia = matched.filter(({ from }) => hasNonImageMedia(from));

        const teaserImageAdded = nonImageMedia.some(
          ({ from: { image: imageFrom }, to: { image: imageTo } }) => !imageFrom && imageTo
        );

        const teaserImageRemoved = nonImageMedia.some(
          ({ from: { image: imageFrom }, to: { image: imageTo } }) => imageFrom && !imageTo
        );

        const teaserImageReplaced = nonImageMedia.some(
          ({ from: { image: imageFrom }, to: { image: imageTo } }) =>
            imageFrom && imageTo && imageFrom.id !== imageTo.id
        );

        const galleryImageReplaced = matched
          .filter(({ from }) => !hasNonImageMedia(from))
          .filter(
            ({ from: { image: imageFrom }, to: { image: imageTo } }) =>
              imageFrom && imageTo && imageFrom.id !== imageTo.id
          ).length;

        const mediaReplaced = matched.filter(({ from, to }) =>
          (['audio', 'video', 'pdf'] as const).some(
            (key) => from[key] && to[key] && from[key]?.id !== to[key]?.id
          )
        ).length;

        const captionChanges = matched.flatMap(({ from, to }) => {
          if ((from.title ?? '') === (to.title ?? '')) {
            return [];
          }

          return textChange({
            label: t('common.caption'),
            from: from.title,
            to: to.title,
          });
        });

        const copyrightChanges = matched.flatMap(({ from, to }) => {
          const imageFrom = from.image;
          const imageTo = to.image;

          if (!imageFrom || !imageTo || imageFrom.id !== imageTo.id) {
            return [];
          }

          if ((imageFrom.rightholder ?? '') === (imageTo.rightholder ?? '')) {
            return [];
          }

          return textChange({
            label: t('common.copyright'),
            from: imageFrom.rightholder,
            to: imageTo.rightholder,
          });
        });

        const parts: string[] = [];

        if (added > 0) {
          parts.push(ctxT('mediaAdded', { count: added, label }));
        }

        if (removed > 0) {
          parts.push(ctxT('mediaRemoved', { count: removed, label }));
        }

        if (teaserImageAdded) {
          parts.push(ctxT('teaserImageAdded', { label }));
        }

        if (teaserImageRemoved) {
          parts.push(ctxT('teaserImageRemoved', { label }));
        }

        if (teaserImageReplaced) {
          parts.push(ctxT('teaserImageReplaced', { label }));
        }

        if (galleryImageReplaced > 0) {
          parts.push(ctxT('galleryImageReplaced', { count: galleryImageReplaced, label }));
        }

        if (mediaReplaced > 0) {
          parts.push(ctxT('mediaReplaced', { count: mediaReplaced, label }));
        }

        parts.push(...captionChanges, ...copyrightChanges);

        return parts.join(', ');
      };

      if (path === 'published') {
        return ctxT(to ? 'published' : 'unpublished');
      }

      if (BOOLEAN_FIELDS.includes(path)) {
        return ctxT('booleanChange', { label, state: ctxT(to ? 'enabled' : 'disabled') });
      }

      if (MARKDOWN_FIELDS.includes(path)) {
        return textChange({
          label,
          from: toContentHtml(from),
          to: toContentHtml(to),
          allowHtml: true,
        });
      }

      if (DATE_FIELDS.includes(path)) {
        return textChange({ label, from: toFormattedDate(from), to: toFormattedDate(to) });
      }

      if (TEXT_FIELDS.includes(path)) {
        return textChange({ label, from, to, allowHtml: true });
      }

      const nameKey = ARRAY_FIELDS[path];

      if (nameKey) {
        return arrayChange(nameKey) || ctxT('updated', { label });
      }

      if (GALLERY_FIELDS.includes(path)) {
        return galleryChange() || ctxT('updated', { label });
      }

      if (IMAGE_FIELDS.includes(path)) {
        if (!from) {
          return ctxT('imageAdded', { label });
        }

        if (!to) {
          return ctxT('imageRemoved', { label });
        }

        return ctxT('imageReplaced', { label });
      }

      if (path === 'location') {
        return textChange({ label, from: toLocationText(from), to: toLocationText(to) });
      }

      if (path === 'story') {
        return ctxT('movedFromStory', {
          title: (from as { title: string }).title,
          interpolation: { escapeValue: false },
        });
      }

      if (path === 'author') {
        return ctxT('authorChanged', {
          name: (to as { screenName: string }).screenName,
          interpolation: { escapeValue: false },
        });
      }

      return ctxT('updated', { label });
    },
    [ctxT, t, textChange, toLocationText]
  );
};
